import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useFieldArray,
  useForm,
  Controller,
  SubmitHandler,
  SubmitErrorHandler,
} from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import {
  Paper,
  Popper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Autocomplete,
} from "@mui/material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import Addicon from "@mui/icons-material/Add";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";

import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";
import useJStyles from "$hooks/useJStyles";
import { usePackageTypesQuery } from "$hooks/usePackageTypesQuery";

import { Button, Text, Modal } from "$components";
import { PackageType, Parcel } from "$types";
import { calculateLoadingMeters } from "$utils/typeConstants/Parcel";

type Props = {
  onSubmit: (data: any) => any;
  parcel: Parcel;
};

type PackageFormValues = {
  typeId: string;
  typeName?: string;
  height?: number;
  length: number;
  width: number;
  weight?: number;
  quantity: number;
  totalWeight?: number;
  key: string;
};

type FormValues = {
  height?: number;
  weight: number;
  quantity: number;
  loadingMeters: number;
  packages: PackageFormValues[];
};

function ParcelForm({ onSubmit, parcel }: Props) {
  const { jclasses } = useJStyles();
  const classes = useStyles();

  const { data: packageTypes = [] } = usePackageTypesQuery();

  const [isValidationModalOpen, toggleValidationModal] =
    useState<boolean>(false);
  const [isTotalWeightFieldLocked, lockTotalWeightField] =
    useState<boolean>(true);
  const defaultParcelValues: FormValues = useMemo(
    () =>
      parcel?.packages?.length > 0
        ? {
            height: parcel.height,
            weight: parcel.weight,
            quantity: parcel.quantity,
            loadingMeters: parcel.loadingMeters,
            packages: parcel.packages.map((packageItem) => ({
              ...packageItem,
              key: uuidv4(),
            })),
          }
        : {
            height: 0,
            weight: 0,
            quantity: 0,
            loadingMeters: 0,
            packages: [
              {
                typeId: null,
                length: 0,
                width: 0,
                weight: 0,
                quantity: 0,
                totalWeight: 0,
                key: uuidv4(),
              },
            ],
          },
    [parcel]
  );

  const { control, handleSubmit, register, watch, setValue, reset } =
    useForm<FormValues>({
      defaultValues: defaultParcelValues,
    });
  const { fields, append, insert, remove, replace } = useFieldArray({
    control,
    name: "packages",
  });
  const watchedPackages = watch("packages");
  const controlledPackageFields = fields.map((packageField, index) => {
    return {
      ...packageField,
      ...watchedPackages[index],
      rowCount: watchedPackages.length,
    };
  });

  // Updates form values if parcel item from useItem changes
  useEffect(() => {
    reset(defaultParcelValues);
  }, [reset, defaultParcelValues]);

  // Calculate total values for total table except total weight
  useEffect(() => {
    const totalPackageFields = controlledPackageFields.reduce(
      (acc, packageFields) => {
        const { length, width, quantity } = packageFields;
        const loadingMeters = calculateLoadingMeters(length, width, quantity);
        acc.loadingMeters += loadingMeters;
        ["height", "quantity", "totalWeight"].forEach((key) => {
          // Get max height value (not the sum)
          if (key === "height") {
            acc[key] =
              (acc[key] || 0) >= (packageFields[key] || 0)
                ? acc[key] || 0
                : packageFields[key] || 0;
          } else {
            acc[key] = (acc[key] || 0) + (packageFields[key] || 0);
          }
        });
        return acc;
      },
      { loadingMeters: 0, height: 0, quantity: 0, totalWeight: 0 }
    );
    setValue("height", totalPackageFields.height);
    setValue(
      "loadingMeters",
      Number(totalPackageFields.loadingMeters.toFixed(2))
    );
    setValue("quantity", totalPackageFields.quantity);
    if (!isTotalWeightFieldLocked) {
      setValue("weight", totalPackageFields.totalWeight);
    }
  }, [setValue, controlledPackageFields, isTotalWeightFieldLocked]);

  // Prevents the user to type a number with more than length characters
  const forceNumberInputValueLength = useCallback(
    (
        inputFormName:
          | keyof FormValues
          | `packages.${number}.${keyof PackageFormValues}`,
        maxLength: number
      ) =>
      (e) => {
        const val = e.target.value;
        setValue(inputFormName, val.slice(0, maxLength));
      },
    [setValue]
  );

  // Checks if the specified attribute key has a fixed value in package types list
  const getFixedAttributeValue = useCallback(
    (packageTypeId: string, attributeKey: keyof PackageFormValues) => {
      const foundIndex = packageTypes.findIndex(
        (packageType) =>
          packageType.id === packageTypeId && packageType[`${attributeKey}`]
      );
      if (foundIndex === -1) {
        return foundIndex;
      }
      return packageTypes[foundIndex][`${attributeKey}`];
    },
    [packageTypes]
  );

  // Checks if at least one field has a value = 0
  const hasZeroValuesInFields = (formValues: FormValues): boolean => {
    return formValues.packages.some((packageItem) =>
      ["length", "width", "height", "weight", "quantity", "totalWeight"].some(
        (packageItemKey) => packageItem[packageItemKey] === 0
      )
    );
  };

  const handleAddLine = useCallback(() => {
    append({
      typeId: null,
      length: 0,
      width: 0,
      height: 0,
      weight: 0,
      quantity: 0,
      totalWeight: 0,
      key: uuidv4(),
    });
  }, [append]);

  const handleDuplicateLine = useCallback(
    (index: number, currentPackage: PackageFormValues) => {
      lockTotalWeightField(false);
      insert(index + 1, {
        typeId: currentPackage.typeId,
        length: currentPackage.length,
        width: currentPackage.width,
        height: currentPackage.height,
        weight: currentPackage.weight,
        quantity: currentPackage.quantity,
        totalWeight: currentPackage.totalWeight,
        key: uuidv4(),
      });
      setTimeout(() => lockTotalWeightField(true), 500);
    },
    [insert, lockTotalWeightField]
  );

  const handleDeleteLine = useCallback(
    (index: number, isOnlyRow = false) => {
      lockTotalWeightField(false);
      if (isOnlyRow) {
        replace([
          {
            typeId: null,
            length: 0,
            width: 0,
            height: 0,
            weight: 0,
            quantity: 0,
            totalWeight: 0,
            key: uuidv4(),
          },
        ]);
      } else {
        remove(index);
      }
      setTimeout(() => lockTotalWeightField(true), 500);
    },
    [replace, remove]
  );

  const onFormSubmit: SubmitHandler<FormValues> = useCallback(
    (formValues: FormValues) => {
      if (hasZeroValuesInFields(formValues)) {
        toggleValidationModal(true);
        return;
      }
      onSubmit(formValues);
    },
    [toggleValidationModal, onSubmit]
  );

  const onFormError: SubmitErrorHandler<FormValues> = useCallback(
    (error) => ({ error }),
    []
  );

  return (
    <form
      className={classes.formContainer}
      id="parcelForm"
      onSubmit={handleSubmit(onFormSubmit, onFormError)}
    >
      <TableContainer className={classes.tableContainer} component={Paper}>
        <TableHead
          style={{
            borderRadius: "3px 3px 0px 0px",
            backgroundColor: "#F4F5F7",
          }}
        >
          <TableCell className={classes.tableHeadCell} width={250}>
            Type*
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Longueur (cm)*
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Largeur (cm)*
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Hauteur
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Poids (kg)
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Quantité
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Poids total (kg)
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80} />
        </TableHead>
        <TableBody>
          {controlledPackageFields.map((currentPackage, index) => {
            const fixedLength: number = getFixedAttributeValue(
              currentPackage.typeId,
              "length"
            );
            const fixedWidth: number = getFixedAttributeValue(
              currentPackage.typeId,
              "width"
            );
            return (
              <TableRow key={currentPackage.key} className={classes.tableRow}>
                <TableCell>
                  <Controller
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        disableClearable
                        options={packageTypes}
                        getOptionLabel={(option: PackageType) => option.label}
                        onChange={(e, val: PackageType) => {
                          // Check if the selected package type attributes have fixed values to set the corresponding form input value
                          ["length", "width"].forEach(
                            (attribute: keyof PackageFormValues) => {
                              const fixedAttributeValue =
                                getFixedAttributeValue(val.id, attribute);
                              if (fixedAttributeValue !== -1) {
                                setValue(
                                  `packages.${index}.${attribute}`,
                                  fixedAttributeValue
                                );
                              }
                            }
                          );
                          onChange(val.id);
                        }}
                        value={
                          packageTypes.find(
                            (packageType) => packageType.id === value
                          ) || null
                        }
                        renderOption={(props, option) => (
                          <li {...props}>
                            <div>{option.label}</div>
                          </li>
                        )}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            required
                            error={!!error}
                            placeholder="Sélectionner un type"
                            {...register(`packages.${index}.typeName`)}
                          />
                        )}
                        PopperComponent={(props: any) => (
                          <Popper
                            {...props}
                            style={{
                              maxWidth: "fit-content",
                            }}
                            placement="bottom-start"
                          />
                        )}
                        style={{ minWidth: "100%" }}
                      />
                    )}
                    name={`packages.${index}.typeId`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value }, fieldState: { error } }) => (
                      <TextField
                        variant="outlined"
                        required
                        type="number"
                        value={fixedLength === -1 ? value : fixedLength}
                        disabled={fixedLength !== -1}
                        {...register(`packages.${index}.length`, {
                          required: true,
                          valueAsNumber: true,
                          min: 0,
                          onChange: forceNumberInputValueLength(
                            `packages.${index}.length`,
                            4
                          ),
                        })}
                        inputProps={{ min: 0 }}
                        error={!!error}
                      />
                    )}
                    name={`packages.${index}.length`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value }, fieldState: { error } }) => (
                      <TextField
                        variant="outlined"
                        required
                        type="number"
                        value={fixedWidth === -1 ? value : fixedWidth}
                        disabled={fixedWidth !== -1}
                        {...register(`packages.${index}.width`, {
                          required: true,
                          valueAsNumber: true,
                          min: 0,
                          onChange: forceNumberInputValueLength(
                            `packages.${index}.width`,
                            4
                          ),
                        })}
                        inputProps={{ min: 0 }}
                        error={!!error}
                      />
                    )}
                    name={`packages.${index}.width`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value } }) => (
                      <TextField
                        variant="outlined"
                        type="number"
                        value={value}
                        {...register(`packages.${index}.height`, {
                          valueAsNumber: true,
                          min: 0,
                          onChange: forceNumberInputValueLength(
                            `packages.${index}.height`,
                            4
                          ),
                        })}
                        inputProps={{ min: 0 }}
                      />
                    )}
                    name={`packages.${index}.height`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value } }) => (
                      <TextField
                        variant="outlined"
                        type="number"
                        value={value}
                        onFocus={() => lockTotalWeightField(false)}
                        {...register(`packages.${index}.weight`, {
                          valueAsNumber: true,
                          min: 0,
                          onChange: (e) => {
                            forceNumberInputValueLength(
                              `packages.${index}.weight`,
                              4
                            )(e);
                            const val = e.target.value;
                            // Update total weight
                            setValue(
                              `packages.${index}.totalWeight`,
                              currentPackage.quantity * val
                            );
                          },
                          onBlur: () => lockTotalWeightField(true),
                        })}
                        defaultValue={0}
                        inputProps={{ min: 0 }}
                      />
                    )}
                    name={`packages.${index}.weight`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value }, fieldState: { error } }) => (
                      <TextField
                        variant="outlined"
                        type="number"
                        value={value}
                        onFocus={() => lockTotalWeightField(false)}
                        {...register(`packages.${index}.quantity`, {
                          required: true,
                          valueAsNumber: true,
                          min: 0,
                          onChange: (e) => {
                            forceNumberInputValueLength(
                              `packages.${index}.quantity`,
                              4
                            )(e);
                            const val = e.target.value;
                            // Update total weight
                            setValue(
                              `packages.${index}.totalWeight`,
                              currentPackage.weight * val
                            );
                          },
                          onBlur: () => lockTotalWeightField(true),
                        })}
                        defaultValue={0}
                        inputProps={{ min: 0 }}
                        error={!!error}
                      />
                    )}
                    name={`packages.${index}.quantity`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <Controller
                    render={({ field: { value } }) => (
                      <TextField
                        variant="outlined"
                        type="number"
                        disabled
                        value={value}
                        {...register(`packages.${index}.totalWeight`, {
                          valueAsNumber: true,
                          min: 0,
                          onChange: forceNumberInputValueLength(
                            `packages.${index}.totalWeight`,
                            4
                          ),
                        })}
                        defaultValue={
                          currentPackage.quantity * currentPackage.weight
                        }
                        inputProps={{ min: 0 }}
                      />
                    )}
                    name={`packages.${index}.totalWeight`}
                    control={control}
                  />
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Button
                      variant="text"
                      onClick={() => handleDuplicateLine(index, currentPackage)}
                      style={{ padding: "8px 16px", borderRadius: 4 }}
                      sx={{
                        minWidth: 50,
                        border: "none !important",
                        color: "black !important",
                        backgroundColor: "transparent !important",
                        "&:hover": {
                          backgroundColor: "#f5f5f5 !important",
                          border: "none !important",
                        },
                        "&.MuiButton-text:hover": {
                          backgroundColor: "#f5f5f5 !important",
                        },
                        "&.MuiButton-root:hover": {
                          backgroundColor: "#f5f5f5 !important",
                        },
                        "& svg": {
                          color: "black !important",
                        },
                      }}
                    >
                      <FileCopyOutlinedIcon />
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        handleDeleteLine(index, currentPackage.rowCount < 2);
                      }}
                      style={{ padding: "8px 16px", borderRadius: 4 }}
                      sx={{
                        minWidth: 50,
                        border: "none !important",
                        color: "black !important",
                        backgroundColor: "transparent !important",
                        "&:hover": {
                          backgroundColor: "#f5f5f5 !important",
                          border: "none !important",
                        },
                        "&.MuiButton-text:hover": {
                          backgroundColor: "#f5f5f5 !important",
                        },
                        "&.MuiButton-root:hover": {
                          backgroundColor: "#f5f5f5 !important",
                        },
                        "& svg": {
                          color: "black !important",
                        },
                      }}
                    >
                      <DeleteForeverOutlinedIcon />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>

      <Button
        variant="text"
        className={classes.addLineButton}
        style={{ margin: "16px 0", padding: "8px 16px", borderRadius: 4 }}
        onClick={handleAddLine}
        sx={{
          padding: 0,
          border: "none !important",
          color: "black !important",
          backgroundColor: "transparent !important",
          "&:hover": {
            backgroundColor: "#f5f5f5 !important",
            border: "none !important",
          },
          "&.MuiButton-text:hover": {
            backgroundColor: "#f5f5f5 !important",
          },
          "&.MuiButton-root:hover": {
            backgroundColor: "#f5f5f5 !important",
          },
          "& svg": {
            color: "black !important",
          },
        }}
      >
        <Addicon style={{ marginRight: 4 }} />
        Ajouter une ligne
      </Button>

      <TableContainer className={classes.tableContainer} component={Paper}>
        <TableHead
          style={{
            borderRadius: "3px 3px 0px 0px",
            backgroundColor: "#F4F5F7",
          }}
        >
          <TableCell className={classes.tableHeadCell} width={250}>
            Total
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={185}>
            Mètre plancher linéaire
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Hauteur (cm)
          </TableCell>
          <TableCell width={80} />
          <TableCell className={classes.tableHeadCell} width={80}>
            Quantité
          </TableCell>
          <TableCell className={classes.tableHeadCell} width={80}>
            Poids total (kg)
          </TableCell>
          <TableCell width={90} />
        </TableHead>
        <TableBody>
          <TableRow className={classes.tableRow}>
            <TableCell />
            <TableCell>
              <Controller
                render={({ field: { value } }) => (
                  <TextField
                    variant="outlined"
                    required
                    type="number"
                    value={value}
                    disabled
                  />
                )}
                name="loadingMeters"
                control={control}
              />
            </TableCell>
            <TableCell>
              <Controller
                render={({ field: { value } }) => (
                  <TextField
                    variant="outlined"
                    required
                    type="number"
                    value={value}
                    disabled
                  />
                )}
                name="height"
                control={control}
              />
            </TableCell>
            <TableCell />
            <TableCell>
              <Controller
                render={({ field: { value } }) => (
                  <TextField
                    variant="outlined"
                    required
                    type="number"
                    value={value}
                    disabled
                  />
                )}
                name="quantity"
                control={control}
              />
            </TableCell>
            <TableCell>
              <Controller
                render={({ field: { value } }) => (
                  <TextField
                    variant="outlined"
                    type="number"
                    value={value}
                    {...register(`weight`, {
                      valueAsNumber: true,
                      min: 0,
                      onChange: forceNumberInputValueLength("weight", 4),
                    })}
                    defaultValue={0}
                    inputProps={{ min: 0 }}
                  />
                )}
                name="weight"
                control={control}
              />
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </TableContainer>
      <Modal
        textValidate="Valider"
        textCancel="Annuler"
        open={isValidationModalOpen}
        onValidate={handleSubmit(onSubmit, onFormError)}
        onCancel={() => toggleValidationModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            Attention
            <br />
            Un ou plusieurs champs ont été complétés avec la valeur 0.
            <br />
            Souhaitez-vous quand même valider le colisage ?
          </Text>
        </div>
      </Modal>
    </form>
  );
}

export default ParcelForm;

const useStyles = createUseStyles(() => ({
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  addLineButton: {
    alignSelf: "flex-end",
  },
  tableContainer: {
    "&.MuiTableContainer-root": {
      flex: 1,
      width: "auto !important",
      padding: theme.spacing(2),
      overflowX: "auto",
    },
  },
  tableHeadCell: {
    "&.MuiTableCell-head": {
      color: theme.color.gray5,
      fontFamily: "Work Sans, sans-serif",
      fontSize: 12,
      textTransform: "uppercase",
      padding: "4px 12px 10px 12px !important",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.color.gray1,
    },
  },
}));
